import React, { useMemo, useState, useEffect, useRef } from "react";
import { Row, Col, Modal, Button } from "react-bootstrap";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";

import CardLessDataTableWithSearch from "../../datatables/CardLessDataTableWithSearch";
import { buildQuestionAnalytics } from "./examReportsHelpers";

function Section({ icon, title, children }) {
    return (
        <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
            <h6 className="font-weight-bold text-dark mb-3 d-flex align-items-center" style={{ gap: 8 }}>
                <i className={icon} style={{ color: "#2563eb" }} />
                {title}
            </h6>
            {children}
        </div>
    );
}

function SelectableSection({ icon, title, childrenTable, childrenGraph, defaultView = "table" }) {
    const [viewMode, setViewMode] = useState(defaultView); // "table" | "graph" | "both"

    return (
        <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center" style={{ gap: 8 }}>
                    <i className={icon} style={{ color: "#2563eb" }} />
                    {title}
                </h6>
                {/* View Mode Selector Buttons */}
                <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 3, display: "inline-flex", gap: 3 }}>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "table" ? "#fff" : "transparent",
                            color: viewMode === "table" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "table" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("table")}
                    >
                        <i className="fal fa-table me-1" /> Table
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "graph" ? "#fff" : "transparent",
                            color: viewMode === "graph" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "graph" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("graph")}
                    >
                        <i className="fal fa-chart-bar me-1" /> Graph
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "both" ? "#fff" : "transparent",
                            color: viewMode === "both" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "both" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("both")}
                    >
                        <i className="fal fa-th-large me-1" /> Both
                    </button>
                </div>
            </div>

            {(viewMode === "table" || viewMode === "both") && childrenTable}
            {viewMode === "both" && <div className="my-3" style={{ borderTop: "1px solid #f1f5f9" }} />}
            {(viewMode === "graph" || viewMode === "both") && childrenGraph}
        </div>
    );
}

function StatCard({ label, value, color, bg, border, icon }) {
    return (
        <div className="col-6 col-sm-4 col-md-2">
            <div
                className="stat-card-item h-100 d-flex align-items-center"
                style={{
                    borderRadius: 12,
                    padding: "12px 14px",
                    border: `1px solid ${border || "#e2e8f0"}`,
                    background: "#fff",
                    gap: 10,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                }}
            >
                <div
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        border: `1px solid ${border || "#e2e8f0"}`,
                    }}
                >
                    <i className={icon} style={{ color, fontSize: 16 }} />
                </div>
                <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", lineHeight: 1.1 }}>{value}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginTop: 2 }}>{label}</div>
                </div>
            </div>
        </div>
    );
}

function EmptyChart({ height = 220 }) {
    return (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ height, fontSize: 13 }}>
            No data available
        </div>
    );
}

function BarChartByName({ data, dataKey, name, color = "#2563eb", height = 240 }) {
    if (!data || data.length === 0) return <EmptyChart height={height} />;
    return (
        <div style={{ width: "100%", height }}>
            <ResponsiveContainer>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip shared={false} />
                    <Bar dataKey={dataKey} name={name} fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: color, fontWeight: 700 }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function HorizontalBarChart({ data, dataKey, name, color = "#7c3aed" }) {
    if (!data || data.length === 0) return <EmptyChart height={220} />;
    const height = Math.max(220, data.length * 32);
    return (
        <div style={{ width: "100%", height }}>
            <ResponsiveContainer>
                <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip shared={false} />
                    <Bar dataKey={dataKey} name={name} fill={color} radius={[0, 4, 4, 0]} isAnimationActive={false} label={{ position: "right", fontSize: 10, fill: color, fontWeight: 700 }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const analysisColumns = (nameLabel) => [
    {
        dataField: "name",
        text: nameLabel,
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 800, color: "#1e293b", fontSize: 12.5 }}>{cell}</span>,
    },
    {
        dataField: "questions",
        text: "Questions",
        sort: true,
        formatter: (cell) => <span style={{ background: "#f1f5f9", color: "#475569", fontWeight: 700, padding: "2px 7px", borderRadius: 12, fontSize: 11 }}>{cell}</span>,
    },
    {
        dataField: "correctPct",
        text: "Correct %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c}%</span>,
    },
    {
        dataField: "wrongPct",
        text: "Wrong %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 800, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c}%</span>,
    },
    {
        dataField: "unattemptedPct",
        text: "Unattempted %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c}%</span>,
    },
    {
        dataField: "errorPct",
        text: "Error %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 700, color: "#0891b2", background: "#ecfeff", border: "1px solid #a5f3fc", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c}%</span>,
    },
];

const getQuestionTableColumns = (onSelectQuestion) => [
    {
        dataField: "slno",
        text: "Sl No",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#64748b" }}>#{cell}</span>,
    },
    {
        dataField: "subject",
        text: "Subject",
        sort: true,
        formatter: (cell) => <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>{cell || "-"}</span>,
    },
    {
        dataField: "chapter",
        text: "Chapter",
        sort: true,
        formatter: (cell) => <span style={{ background: "#ecfeff", color: "#0891b2", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>{cell || "-"}</span>,
    },
    {
        dataField: "topic",
        text: "Topic",
        sort: true,
        formatter: (cell) => <span style={{ background: "#f0fdf4", color: "#16a34a", padding: "2px 7px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>{cell || "-"}</span>,
    },
    {
        dataField: "question",
        text: "Question",
        formatter: (cell, row) => {
            const content = cell || row.questionText || "";
            return (
                <div
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelectQuestion(row)}
                    title="Click to view full question details"
                >
                    <div
                        className="question-math-render"
                        style={{ fontSize: 13, color: "#1e293b", maxHeight: 65, overflowY: "auto", minWidth: 200 }}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                    <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 700, marginTop: 4 }}>
                        <i className="fal fa-eye me-1" />View Details
                    </div>
                </div>
            );
        },
    },
    { dataField: "takers", text: "Students Appeared", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#475569" }}>{cell ?? "-"}</span> },
    // { dataField: "correct", text: "Correct", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#15803d" }}>{cell ?? 0}</span> },
    // { dataField: "wrong", text: "Wrong", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#b91c1c" }}>{cell ?? 0}</span> },
    // { dataField: "unattempted", text: "Unattempted", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#b45309" }}>{cell ?? 0}</span> },
    {
        dataField: "correctPct",
        text: "Correct %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c ?? 0}%</span>,
    },
    {
        dataField: "wrongPct",
        text: "Wrong %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 800, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c ?? 0}%</span>,
    },
    {
        dataField: "unattemptedPct",
        text: "Unattempted %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c ?? 0}%</span>,
    },
    {
        dataField: "errorPct",
        text: "Error %",
        sort: true,
        formatter: (c) => <span style={{ fontWeight: 700, color: "#0891b2", background: "#ecfeff", border: "1px solid #a5f3fc", padding: "2px 7px", borderRadius: 6, fontSize: 11.5 }}>{c ?? 0}%</span>,
    },
    {
        dataField: "actions",
        text: "Action",
        formatter: (cell, row) => (
            <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 8, padding: "3px 10px", whiteSpace: "nowrap" }}
                onClick={() => onSelectQuestion(row)}
            >
                <i className="fal fa-eye me-1" /> View Details
            </button>
        ),
    },
];

function QuestionModal({ question, onClose, onPrev, onNext, position, total }) {
    const q = question || {};
    const content = q.question || q.questionText || "No Question Text";
    const correctAns = String(q.correctOption || q.answer || q.correct_answer || "").trim();
    const bodyRef = useRef(null);

    // Stepping to another question must start at the top of the card — otherwise you land
    // mid-way down the previous question's options.
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [q.questionId, q.slno]);

    const normalizedOptions = useMemo(() => {
        if (!question) return [];
        if (Array.isArray(question.options) && question.options.length > 0) {
            return question.options.map((opt, idx) => {
                const key = opt.optionKey || String.fromCharCode(65 + idx);
                const text = opt.optionText || opt.text || `Option ${idx + 1}`;
                const count = opt.count ?? 0;
                const pct = opt.pct != null ? Number(opt.pct) : (question.takers ? Number(((count / question.takers) * 100).toFixed(1)) : 0);
                const isCorrect = opt.isCorrect ?? (correctAns && (correctAns.toUpperCase() === key || correctAns === String(idx + 1) || correctAns === text));
                return { key, text, count, pct, isCorrect };
            });
        }

        const rawList = [
            { text: question.option1 || question.option_1, count: question.option1Count ?? question.option1_count },
            { text: question.option2 || question.option_2, count: question.option2Count ?? question.option2_count },
            { text: question.option3 || question.option_3, count: question.option3Count ?? question.option3_count },
            { text: question.option4 || question.option_4, count: question.option4Count ?? question.option4_count },
        ].filter((o) => o.text);

        const takers = question.takers || rawList.reduce((sum, o) => sum + (o.count || 0), 0);

        return rawList.map((opt, idx) => {
            const key = String.fromCharCode(65 + idx);
            const isCorrect = correctAns && (
                correctAns.toUpperCase() === key ||
                correctAns === String(idx + 1) ||
                correctAns.toLowerCase() === `option${idx + 1}` ||
                correctAns === opt.text
            );
            const count = opt.count ?? 0;
            const pct = takers > 0 ? Number(((count / takers) * 100).toFixed(1)) : 0;
            return { key, text: opt.text, count, pct, isCorrect };
        });
    }, [question, correctAns]);

    if (!question) return null;

    return (
        <Modal show={true} onHide={onClose} size="lg" centered scrollable dialogClassName="lecturer-dashboard-modern" style={{ zIndex: 1080 }}>
            <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9" }}>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="fal fa-question-circle" style={{ color: "#2563eb", fontSize: 16 }} />
                    </div>
                    <span>Question Details {question.slno ? `#${question.slno}` : ""}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body ref={bodyRef} style={{ padding: "20px 24px" }}>
                <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
                    {question.subject && (
                        <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                            📚 {question.subject}
                        </span>
                    )}
                    {question.chapter && (
                        <span style={{ background: "#ecfeff", color: "#0891b2", border: "1px solid #a5f3fc", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                            📖 {question.chapter}
                        </span>
                    )}
                    {question.topic && (
                        <span style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                            🏷️ {question.topic}
                        </span>
                    )}
                    {/* {question.quality && (
                        <span style={{
                            background: question.quality === "OK" ? "#f0fdf4" : question.quality === "Warning" ? "#fffbeb" : "#fef2f2",
                            color: question.quality === "OK" ? "#15803d" : question.quality === "Warning" ? "#b45309" : "#b91c1c",
                            border: `1px solid ${question.quality === "OK" ? "#bbf7d0" : question.quality === "Warning" ? "#fde68a" : "#fecaca"}`,
                            padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700
                        }}>
                            Status: {question.quality}
                        </span>
                    )} */}
                </div>

                <div className="mb-4" style={{ background: "#f8fafc", borderRadius: 12, padding: "16px 20px", border: "1px solid #e2e8f0" }}>
                    <h6 style={{ fontSize: 12.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                        Question Statement
                    </h6>
                    <div
                        className="question-math-render"
                        style={{ fontSize: 14.5, color: "#0f172a", lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </div>

                {normalizedOptions.length > 0 && (
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 style={{ fontSize: 12.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0 }}>
                                Options &amp; Student Choice Counts
                            </h6>
                            <span className="text-muted" style={{ fontSize: 11.5, fontWeight: 600 }}>
                                Total Takers: {question.takers || 0}
                            </span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {normalizedOptions.map((opt, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        background: opt.isCorrect ? "#f0fdf4" : "#fff",
                                        border: `1px solid ${opt.isCorrect ? "#86efac" : "#e2e8f0"}`,
                                        borderRadius: 10,
                                        padding: "12px 16px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        boxShadow: opt.isCorrect ? "0 2px 8px rgba(22, 163, 74, 0.08)" : "none",
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 10 }}>
                                        <div className="d-flex align-items-center" style={{ gap: 12, flex: 1 }}>
                                            <div
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: "50%",
                                                    background: opt.isCorrect ? "#16a34a" : "#f1f5f9",
                                                    color: opt.isCorrect ? "#fff" : "#475569",
                                                    fontWeight: 800,
                                                    fontSize: 13,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {opt.key}
                                            </div>
                                            <div
                                                className="question-math-render"
                                                style={{ flex: 1, fontSize: 14, color: "#1e293b", fontWeight: opt.isCorrect ? 700 : 500 }}
                                                dangerouslySetInnerHTML={{ __html: opt.text }}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            {opt.isCorrect && (
                                                <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 12 }}>
                                                    ✓ Correct Answer
                                                </span>
                                            )}
                                            <span style={{ background: opt.isCorrect ? "#e6f4ea" : "#f1f5f9", color: opt.isCorrect ? "#137333" : "#475569", fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 12 }}>
                                                {opt.count} Attempted ({opt.pct}%)
                                            </span>
                                        </div>
                                    </div>

                                    {/* Response percentage progress bar */}
                                    <div style={{ height: 5, borderRadius: 3, background: "#f1f5f9", overflow: "hidden", width: "100%" }}>
                                        <div
                                            style={{
                                                width: `${Math.min(100, Math.max(0, opt.pct))}%`,
                                                height: "100%",
                                                background: opt.isCorrect ? "#16a34a" : "#64748b",
                                                borderRadius: 3,
                                                transition: "width 0.3s ease",
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {(question.solution || question.explanation) && (
                    <div className="mb-4" style={{ background: "#eff6ff", borderRadius: 12, padding: "14px 18px", border: "1px solid #bfdbfe" }}>
                        <h6 style={{ fontSize: 12.5, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                            💡 Explanation / Solution
                        </h6>
                        <div
                            className="question-math-render"
                            style={{ fontSize: 13.5, color: "#1e3a8a", lineHeight: 1.6 }}
                            dangerouslySetInnerHTML={{ __html: question.solution || question.explanation }}
                        />
                    </div>
                )}

                <div>
                    <h6 style={{ fontSize: 12.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                        Performance Breakdown
                    </h6>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                        <div style={{ background: "#f8fafc", borderRadius: 12, padding: "12px 14px", border: "1px solid #e2e8f0" }}>
                            <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 600 }}>Students Appeared</p>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2937", lineHeight: 1.2, marginTop: 2 }}>{question.takers ?? 0}</div>
                        </div>
                        <div style={{ background: "#f0fdf4", borderRadius: 12, padding: "12px 14px", border: "1px solid #bbf7d0" }}>
                            <p style={{ margin: 0, fontSize: 11, color: "#15803d", fontWeight: 600 }}>Correct ({question.correctPct ?? 0}%)</p>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "#16a34a", lineHeight: 1.2, marginTop: 2 }}>{question.correct ?? 0}</div>
                        </div>
                        <div style={{ background: "#fef2f2", borderRadius: 12, padding: "12px 14px", border: "1px solid #fecaca" }}>
                            <p style={{ margin: 0, fontSize: 11, color: "#b91c1c", fontWeight: 600 }}>Wrong ({question.wrongPct ?? 0}%)</p>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "#dc2626", lineHeight: 1.2, marginTop: 2 }}>{question.wrong ?? 0}</div>
                        </div>
                        <div style={{ background: "#fffbeb", borderRadius: 12, padding: "12px 14px", border: "1px solid #fde68a" }}>
                            <p style={{ margin: 0, fontSize: 11, color: "#b45309", fontWeight: 600 }}>Unattempted ({question.unattemptedPct ?? 0}%)</p>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "#d97706", lineHeight: 1.2, marginTop: 2 }}>{question.unattempted ?? 0}</div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", justifyContent: "space-between" }}>
                {/* Step through the paper without closing the card. Order follows the
                    question numbers, and the buttons disable at either end. */}
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <Button
                        variant="outline-primary"
                        disabled={!onPrev}
                        onClick={onPrev || undefined}
                        style={{ borderRadius: 8, fontWeight: 700, fontSize: 12.5 }}
                    >
                        <i className="fal fa-chevron-left me-1" /> Previous
                    </Button>
                    <Button
                        variant="outline-primary"
                        disabled={!onNext}
                        onClick={onNext || undefined}
                        style={{ borderRadius: 8, fontWeight: 700, fontSize: 12.5 }}
                    >
                        Next <i className="fal fa-chevron-right ms-1" />
                    </Button>
                </div>
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    {total > 0 && position > 0 && (
                        <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                            Question {position} of {total}
                        </span>
                    )}
                    <Button variant="secondary" onClick={onClose} style={{ borderRadius: 8, fontWeight: 700, fontSize: 12.5 }}>
                        Close
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

function QuestionAnalysisTab({ data, loading, error, authorizedSubjectNames }) {
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [questionOrderMode, setQuestionOrderMode] = useState("start"); // "middle" | "start"

    const analytics = useMemo(() => buildQuestionAnalytics(data, authorizedSubjectNames), [data, authorizedSubjectNames]);
    const questionColumns = useMemo(() => getQuestionTableColumns(setSelectedQuestion), []);

    const midSlno = useMemo(() => {
        if (!analytics.questions || !analytics.questions.length) return 1;
        const slnos = analytics.questions.map((q) => q.slno || 0).filter(Boolean);
        if (!slnos.length) return Math.ceil(analytics.questions.length / 2);
        const min = Math.min(...slnos);
        const max = Math.max(...slnos);
        return Math.round((min + max) / 2);
    }, [analytics.questions]);

    const orderedQuestions = useMemo(() => {
        if (!analytics.questions || !analytics.questions.length) return [];
        if (questionOrderMode === "start") {
            return [...analytics.questions].sort((a, b) => (a.slno || 0) - (b.slno || 0));
        }
        // "middle" order mode: Display middle questions first in starting rows
        return [...analytics.questions].sort((a, b) => {
            const distA = Math.abs((a.slno || 0) - midSlno);
            const distB = Math.abs((b.slno || 0) - midSlno);
            if (distA !== distB) return distA - distB;
            return (a.slno || 0) - (b.slno || 0);
        });
    }, [analytics.questions, questionOrderMode, midSlno]);

    // Where the open question sits in the question-number order, so the details modal can
    // walk to the previous / next one. Matched on id (the row objects survive the table's
    // search + sort, but an id match is cheap insurance).
    const selectedIndex = useMemo(() => {
        if (!selectedQuestion) return -1;
        const keyOf = (q) => String(q.id != null ? q.id : q.questionId != null ? q.questionId : q.slno);
        return orderedQuestions.findIndex((q) => q === selectedQuestion || keyOf(q) === keyOf(selectedQuestion));
    }, [orderedQuestions, selectedQuestion]);

    const stepQuestion = (delta) => {
        const next = orderedQuestions[selectedIndex + delta];
        if (next) setSelectedQuestion(next);
    };

    if (loading) {
        return (
            <div>
                {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="lecture-exam-shim w-100 mb-3" style={{ height: 90 }} />
                ))}
            </div>
        );
    }
    if (error) {
        return <p className="text-danger text-center py-5">Unable to load question analysis.</p>;
    }
    if (!analytics.questions.length) {
        return (
            <div className="text-center py-5">
                <i className="fal fa-question-circle text-muted" style={{ fontSize: 32 }} />
                <p className="text-muted mt-2 mb-0">No question analysis data available.</p>
            </div>
        );
    }

    const { overall } = analytics;

    return (
        <>
            <Section icon="fal fa-chart-pie" title="Overview">
                <div className="row g-2">
                    <StatCard label="Total Questions" value={overall.totalQuestions} color="#1d4ed8" bg="#eff6ff" border="#bfdbfe" icon="fal fa-list-ol" />
                    <StatCard label="Students Appeared" value={overall.takers} color="#7c3aed" bg="#f5f3ff" border="#ddd6fe" icon="fal fa-users" />
                    <StatCard label="Correct %" value={`${overall.correctPct}%`} color="#15803d" bg="#f0fdf4" border="#bbf7d0" icon="fal fa-check-circle" />
                    <StatCard label="Wrong %" value={`${overall.wrongPct}%`} color="#b91c1c" bg="#fef2f2" border="#fecaca" icon="fal fa-times-circle" />
                    <StatCard label="Unattempted %" value={`${overall.unattemptedPct}%`} color="#b45309" bg="#fffbeb" border="#fde68a" icon="fal fa-forward" />
                    <StatCard label="Error %" value={`${overall.errorPct}%`} color="#0891b2" bg="#ecfeff" border="#a5f3fc" icon="fal fa-exclamation-triangle" />
                </div>
            </Section>

            <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                    <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                        <i className="fal fa-table" style={{ color: "#2563eb" }} />
                        Question Analysis
                        {/* <span className="badge bg-light text-primary border" style={{ fontSize: 11, fontWeight: 700 }}>
                            {questionOrderMode === "middle" ? `Displaying Middle Questions First (Q#${midSlno})` : "Displaying from Q1"}
                        </span> */}
                    </h6>
                    {/* <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 3, display: "inline-flex", gap: 3 }}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: questionOrderMode === "middle" ? "#2563eb" : "transparent",
                                color: questionOrderMode === "middle" ? "#fff" : "#64748b",
                                boxShadow: questionOrderMode === "middle" ? "0 1px 4px rgba(37,99,235,0.3)" : "none",
                            }}
                            onClick={() => setQuestionOrderMode("middle")}
                        >
                            🎯 Middle Questions First
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: questionOrderMode === "start" ? "#2563eb" : "transparent",
                                color: questionOrderMode === "start" ? "#fff" : "#64748b",
                                boxShadow: questionOrderMode === "start" ? "0 1px 4px rgba(37,99,235,0.3)" : "none",
                            }}
                            onClick={() => setQuestionOrderMode("start")}
                        >
                            🔢 Start from Q1
                        </button>
                    </div> */}
                </div>

                <CardLessDataTableWithSearch
                    parentData={orderedQuestions}
                    particlecolumns={questionColumns}
                    name="question_analysis"
                    placeholder="Search questions..."
                />
            </div>

            <Row className="g-3 mb-0">
                {/* Subject Analysis - col 12 */}
                <Col md={12}>
                    <SelectableSection
                        icon="fal fa-book"
                        title="Subject Analysis"
                        defaultView="table"
                        childrenTable={
                            <CardLessDataTableWithSearch
                                parentData={analytics.subjectAnalysis}
                                particlecolumns={analysisColumns("Subject")}
                                defaultSorted={[{ dataField: "correctPct", order: "desc" }]}
                                name="subject_analysis"
                                placeholder="Search subjects..."
                            />
                        }
                        childrenGraph={
                            <BarChartByName data={analytics.subjectAnalysis} dataKey="correctPct" name="Correct %" color="#2563eb" height={260} />
                        }
                    />
                </Col>

                {/* Chapter Analysis - col 6 */}
                <Col md={6}>
                    <SelectableSection
                        icon="fal fa-bookmark"
                        title="Chapter Analysis"
                        defaultView="table"
                        childrenTable={
                            <CardLessDataTableWithSearch
                                parentData={analytics.chapterAnalysis}
                                particlecolumns={analysisColumns("Chapter")}
                                defaultSorted={[{ dataField: "correctPct", order: "desc" }]}
                                name="chapter_analysis"
                                placeholder="Search chapters..."
                            />
                        }
                        childrenGraph={
                            <HorizontalBarChart data={analytics.chapterAnalysis} dataKey="correctPct" name="Correct %" color="#7c3aed" />
                        }
                    />
                </Col>

                {/* Topic Analysis - col 6 */}
                <Col md={6}>
                    <SelectableSection
                        icon="fal fa-tag"
                        title="Topic Analysis"
                        defaultView="table"
                        childrenTable={
                            <CardLessDataTableWithSearch
                                parentData={analytics.topicAnalysis}
                                particlecolumns={analysisColumns("Topic")}
                                defaultSorted={[{ dataField: "correctPct", order: "desc" }]}
                                name="topic_analysis"
                                placeholder="Search topics..."
                            />
                        }
                        childrenGraph={
                            <HorizontalBarChart data={analytics.topicAnalysis} dataKey="correctPct" name="Correct %" color="#16a34a" />
                        }
                    />
                </Col>
            </Row>

            {/* Question Details Modal */}
            <QuestionModal
                question={selectedQuestion}
                onClose={() => setSelectedQuestion(null)}
                position={selectedIndex + 1}
                total={orderedQuestions.length}
                onPrev={selectedIndex > 0 ? () => stepQuestion(-1) : null}
                onNext={selectedIndex >= 0 && selectedIndex < orderedQuestions.length - 1 ? () => stepQuestion(1) : null}
            />
        </>
    );
}

export default QuestionAnalysisTab;
